<script setup lang="ts">
import { loadStripe, type StripeEmbeddedCheckout } from '@stripe/stripe-js'
import { stripeClientSecretOf } from '~/utils/checkoutEmbed'

/**
 * The checkout inside our page, whichever takes the payment: LemonSqueezy's
 * in an iframe, or Stripe's Embedded Checkout for the Czech and Polish
 * checkouts in crowns and złoty (`src` from utils/checkoutEmbed.ts). The flows around it watch for
 * the webhook either way, so a payment finished here is noticed on its own.
 */
const props = defineProps<{
  src: string
  /** Classes for the LemonSqueezy iframe. */
  frameClass?: string
}>()

const { t } = useI18n()
const config = useRuntimeConfig()
const target = ref<HTMLElement | null>(null)
const failed = ref(false)
const clientSecret = computed(() => stripeClientSecretOf(props.src))

/** Stripe allows one Embedded Checkout on a page at a time. */
let checkout: StripeEmbeddedCheckout | null = null

function destroy() {
  checkout?.destroy()
  checkout = null
}

async function show() {
  destroy()
  failed.value = false
  const secret = clientSecret.value
  if (!secret || !target.value) return
  try {
    const stripe = await loadStripe(config.public.stripePublishableKey as string)
    if (!stripe) throw new Error('Stripe.js did not load')
    const instance = await stripe.initEmbeddedCheckout({ fetchClientSecret: async () => secret })
    // Closed or replaced while Stripe was loading.
    if (secret !== clientSecret.value || !target.value) {
      instance.destroy()
      return
    }
    checkout = instance
    instance.mount(target.value)
  } catch (err) {
    console.error('Stripe checkout could not be shown:', err)
    failed.value = true
  }
}

onMounted(show)
watch(clientSecret, () => nextTick(show))
onBeforeUnmount(destroy)
</script>

<template>
  <iframe v-if="!clientSecret" :src="src" :class="frameClass ?? 'relative size-full'" allow="payment" frameborder="0" />
  <div v-else class="relative size-full overflow-y-auto">
    <div ref="target" class="min-h-full" />
    <p v-if="failed" class="absolute inset-x-0 top-1/2 px-6 text-center text-base font-semibold text-ink dark:text-white">
      {{ t('premium.checkoutError') }}
    </p>
  </div>
</template>
